<template>
    <div :class="[
    'custom-treeview',
    {'custom-treeview--hoverable': this.hoverable,
    'custom-treeview--dense': this.dense}
    ]">
        <ul style="list-style: none;padding-left: 0;padding-right: 0.5rem;">
            <custom-tree-node
                    v-for="(item, index) in itemdata"
                    :key="index"
                    :item="item"
                    :item-text="itemText"
                    :item-key="itemKey"
                    :show-value-text="showValueText"
                    :item-disabled="itemDisabled"
                    :edit-column-style="editColumnStyle"
                    :show-edit-column="showEditColumn"
                    :show-add-button="showAddButton"
                    :show-edit-button="showEditButton"
                    :show-remove-button="showRemoveButton"
                    :show-check-box="showCheckBox"
                    :item-text-class="itemTextClass"
                    :item-text-style="itemTextStyle"
                    :select-all="selectAll"
                    :on-hover-action="onHoverAction">
            </custom-tree-node>
        </ul>
    </div>

</template>

<script>
    import CustomTreeNode from "./CustomTreeNode";

    export default {
        name: "CustomTreeView",
        components: {
            CustomTreeNode,
        },

        provide() {
            return {
                treeview: this
            }
        },

        props: {
            active: {
                type: Array,
                default: () => ([]),
            },
            dense: Boolean,
            // filter: Function as PropValidator < FilterTreeItemFunction >,
            hoverable: Boolean,
            items: {
                type: Array,
                default: () => ([]),
            },
            multipleActive: Boolean,
            multiple: Boolean,
            open: {
                type: Array,
                default: () => ([]),
            },
            openAll: Boolean,
            returnObject: {
                type: Boolean,
                default: false,
            },
            search: String,
            selectionType: {
                type: String,
                default: 'leaf',
                validator: (v) => ['leaf', 'independent'].includes(v),
            },
            value: {
                type: Array,
                default: () => ([]),
            },
            activatable: Boolean,
            activeClass: {
                type: String,
                default: 'v-treeview-node--active',
            },
            color: {
                type: String,
                default: 'primary',
            },
            expandIcon: {
                type: String,
                default: '$subgroup',
            },
            indeterminateIcon: {
                type: String,
                default: '$checkboxIndeterminate',
            },
            itemChildren: {
                type: String,
                default: 'children',
            },
            itemDisabled: {
                type: String,
                default: 'false',
            },
            itemKey: {
                type: String,
                default: 'id',
            },
            itemValueText: {
                type: String,
                default: 'value',
            },
            itemText: {
                type: String,
                default: 'name',
            },
            loadChildren: Function,
            loadingIcon: {
                type: String,
                default: '$loading',
            },
            offIcon: {
                type: String,
                default: '$checkboxOff',
            },
            onIcon: {
                type: String,
                default: '$checkboxOn',
            },
            openOnClick: Boolean,
            rounded: Boolean,
            selectable: Boolean,
            selectedColor: {
                type: String,
                default: 'accent',
            },
            shaped: Boolean,
            transition: Boolean,
            editColumnStyle: {
                type: Array,
                default: () => [{'width':'380px','min-width':'380px'}]
            },
            showEditColumn: {
                type: Boolean,
                default: false,
            },
            showAddButton: {
                type: Boolean,
                default: true,
            },
            showEditButton: {
                type: Boolean,
                default: true,
            },
            showRemoveButton: {
                type: Boolean,
                default: true,
            },
            showCheckBox: {
                type: Boolean,
                default: true,
            },
            onHoverAction: {
                type: Boolean,
                default: false,
            },
            itemTextClass: {
                type: Array,
                default: () => ([]),
            },
            itemTextStyle: {
                type: Array,
                default: () => ([]),
            },
            oldNodes:{
                type: Object,
                default:() => ({}),
            },
            selectionMode:{
                type: String,
                default: 'deepest'
            },
            showValueText:{
                type: Boolean,
                default: false,
            },
            selectAll: {
                type: Boolean,
                default: true,
            }
        },

        data() {
            return {
                activeCache: new Set(),
                nodes: this.oldNodes,
                openCache: new Set(),
                selectedCache: new Set(),
                itemdata: this.items || [],
                dataSelectionType: this.selectionType || 'leaf',
            }
        },

        computed: {
            excludedItems() {
                const excluded = new Set();

                if (!this.search) return excluded;

                for (let i = 0; i < this.itemdata.length; i++) {
                    filterTreeItems(
                        this.filter || filterTreeItem,
                        this.itemdata[i],
                        this.search,
                        this.itemKey,
                        this.itemText,
                        this.itemChildren,
                        excluded
                    )
                }
                return excluded
            },
        },

        watch: {
            items: {
                handler() {
                    const oldKeys = Object.keys(this.nodes).map(k => this.getObjectValueByPath(this.nodes[k].item, this.itemKey));
                    const newKeys = this.getKeys(this.itemdata);
                    const diff = this.arrayDiff(newKeys, oldKeys);

                    // We only want to do stuff if items have changed
                    if (!diff.length && newKeys.length < oldKeys.length) return;

                    // If nodes are removed we need to clear them from this.nodes
                    diff.forEach(k => delete this.nodes[k]);

                    const oldSelectedCache = [...this.selectedCache];
                    this.selectedCache = new Set();
                    this.activeCache = new Set();
                    this.openCache = new Set();
                    this.buildTree(this.itemdata);

                    // Only emit selected if selection has changed
                    // as a result of items changing. This fixes a
                    // potential double emit when selecting a node
                    // with dynamic children
                    if (!this.deepEqual(oldSelectedCache, [...this.selectedCache])) this.emitSelected()
                },
                deep: true,
            },
            itemdata: {
                handler() {
                    const oldKeys = Object.keys(this.nodes).map(k => this.getObjectValueByPath(this.nodes[k].item, this.itemKey));
                    const newKeys = this.getKeys(this.itemdata);
                    const diff = this.arrayDiff(newKeys, oldKeys);

                    // We only want to do stuff if items have changed
                    if (!diff.length && newKeys.length < oldKeys.length) return;

                    // If nodes are removed we need to clear them from this.nodes
                    diff.forEach(k => delete this.nodes[k]);

                    const oldSelectedCache = [...this.selectedCache];
                    this.selectedCache = new Set();
                    this.activeCache = new Set();
                    this.openCache = new Set();
                    this.buildTree(this.itemdata);

                    // Only emit selected if selection has changed
                    // as a result of items changing. This fixes a
                    // potential double emit when selecting a node
                    // with dynamic children
                    if (!this.deepEqual(oldSelectedCache, [...this.selectedCache])) this.emitSelected()
                },
                deep: true,
            },
            active(value) {
                this.handleNodeCacheWatcher(value, this.activeCache, this.updateActive, this.emitActive)
            },

            value(value) {
                this.handleNodeCacheWatcher(value, this.selectedCache, this.updateSelected, this.emitSelected)
            },

            open(value) {
                this.handleNodeCacheWatcher(value, this.openCache, this.updateOpen, this.emitOpen)
            },

        },

        beforeCreate() {
        },
        created() {
            if (!this.multiple) {
                this.dataSelectionType = 'independent';
            }
            this.buildTree(this.itemdata);
            if (this.multiple) {
                this.value.forEach(key => this.updateSelected(key, true))
            } else {
                if (this.value) {
                    this.updateSelected(this.value[0], true)
                }
            }
            this.active.forEach(key => this.updateActive(key, true))
        },

        mounted() {
            if (this.$slots.prepend || this.$slots.append) {
                consoleWarn('The prepend and append slots require a slot-scope attribute', this)
            }

            if (this.openAll) {
                this.updateAll(true);
            } else {
                if (this.openCache.size > 0) {
                    this.openCache.forEach(key => this.updateOpen(key, true));
                } else {
                    let selection = new Set();
                    let openArray = Array.isArray(this.open) && this.open.length > 0 ? this.open : this.value;
                    openArray.forEach(openKey => {
                        let tmp = this.nodes[openKey];
                        if(tmp) {
                            while (tmp.parent) {
                                let parent = tmp.parent;
                                tmp = this.nodes[parent];
                                selection.add(parent);
                            }
                        }
                    });
                    let margeArray = this.open.concat(Array.from(selection));
                    margeArray.forEach(key => this.updateOpen(key, true));
                }
                this.emitOpen();
            }
        },

        updated() {
            if (this.openAll) {
                this.updateAll(true);
            } else {
                if (this.openCache.size > 0) {
                    this.openCache.forEach(key => this.updateOpen(key, true));
                } else {
                    let selection = new Set();
                    let openArray = Array.isArray(this.open) && this.open.length > 0 ? this.open : this.value;
                    openArray.forEach(openKey => {
                        let tmp = this.nodes[openKey];
                        while (tmp.parent) {
                            let parent = tmp.parent;
                            tmp = this.nodes[parent];
                            selection.add(parent);
                        }
                    });
                    let margeArray = this.open.concat(Array.from(selection));
                    margeArray.forEach(key => this.updateOpen(key, true));
                }
                this.emitOpen();
            }
            this.log('updated');
        },
        destroyed(){
            this.$emit('destroyed-treeview', this.selectedCache,  this.nodes);
        },
        methods: {
            updateAll(value) {
                Object.keys(this.nodes).forEach(key => this.updateOpen(this.getObjectValueByPath(this.nodes[key].item, this.itemKey), value));
                this.emitOpen()
            },
            getKeys(items) {
                let keys = [];
                for (let i = 0; i < items.length; i++) {
                    const key = this.getObjectValueByPath(items[i], this.itemKey);
                    keys.push(key);
                    const children = this.getObjectValueByPath(items[i], this.itemChildren);
                    if (children) {
                        keys.push(...this.getKeys(children))
                    }
                }
                return keys
            },
            buildTree(items, parent) {
                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    const key = this.getObjectValueByPath(item, this.itemKey);
                    const children = this.getObjectValueByPath(item, this.itemChildren, []);
                    const oldNode = this.nodes.hasOwnProperty(key) ? this.nodes[key] : {
                        isSelected: false, isIndeterminate: false, isActive: false, isOpen: false, vnode: null,
                    };
                    oldNode.isSelected = false;

                    const node = {
                        vnode: oldNode.vnode,
                        parent,
                        children: children.map((c) => this.getObjectValueByPath(c, this.itemKey)),
                        item,
                    };

                    this.buildTree(children, key);

                    // This fixed bug with dynamic children resetting selected parent state
                    if (!this.nodes.hasOwnProperty(key) && parent !== null && this.nodes.hasOwnProperty(parent)) {
                        node.isSelected = this.nodes[parent].isSelected;
                        node.isIndeterminate = this.nodes[parent].isIndeterminate;
                    } else {
                        node.isSelected = oldNode.isSelected;
                        node.isIndeterminate = oldNode.isIndeterminate;
                    }

                    node.isActive = oldNode.isActive;
                    node.isOpen = oldNode.isOpen;

                    this.nodes[key] = !children.length ? node : this.calculateState(node, this.nodes);

                    // Don't forget to rebuild cache
                    if (this.nodes[key].isSelected) {
                        this.selectedCache.add(key);
                    }
                    if (this.nodes[key].isActive) {
                        this.activeCache.add(key);
                    }
                    if (this.nodes[key].isOpen) {
                        this.openCache.add(key);
                    }

                    this.updateVnodeState(key);
                }
            },
            calculateState(node, state) {
                const counts = node.children.reduce((counts, child) => {
                    counts[0] += +Boolean(state[child].isSelected);
                    counts[1] += +Boolean(state[child].isIndeterminate);
                    return counts;
                }, [0, 0]);

                node.isSelected = !!node.children.length && counts[0] === node.children.length;
                node.isIndeterminate = !node.isSelected && (counts[0] > 0 || counts[1] > 0);

                return node
            },
            emitOpen() {
                this.emitNodeCache('update:open', this.openCache);
            },
            emitSelected() {
                this.emitNodeCache('input', this.selectedCache);
                this.$emit('emit-selected', this.selectedCache,  this.nodes);
            },
            emitActive() {
                this.emitNodeCache('update:active', this.activeCache);
            },
            emitNodeCache(event, cache) {
                this.$emit(event, this.returnObject ? [...cache].map(key => this.nodes[key].item) : [...cache]);
            },
            handleNodeCacheWatcher(value, cache, updateFn, emitFn) {
                value = this.returnObject ? value.map(v => this.getObjectValueByPath(v, this.itemKey)) : value;
                const old = [...cache];
                if (this.deepEqual(old, value)) return;

                old.forEach(key => updateFn(key, false));
                value.forEach(key => updateFn(key, true));

                emitFn();
            },
            getDescendants(key, descendants = []) {
                const children = this.nodes[key].children;

                descendants.push(...children);

                for (let i = 0; i < children.length; i++) {
                    descendants = this.getDescendants(children[i], descendants);
                }
                return descendants;
            },
            getParents(key) {
                let parent = this.nodes[key].parent;

                const parents = [];
                while (parent != null) {
                    parents.push(parent);
                    parent = this.nodes[parent].parent;
                }

                return parents;
            },
            getParent(key) {
                let parent = this.nodes[key].parent;
                return parent;
            },
            register(node) {
                const key = this.getObjectValueByPath(node.item, this.itemKey);
                this.nodes[key].vnode = node;
                this.updateVnodeState(key);
            },
            unregister(node) {
                const key = this.getObjectValueByPath(node.item, this.itemKey);
                if (this.nodes[key]) this.nodes[key].vnode = null;
            },
            isParent(key) {
                return this.nodes[key].children && this.nodes[key].children.length;
            },
            updateActive(key, isActive) {
                if (!this.nodes.hasOwnProperty(key)) return;

                if (!this.multipleActive) {
                    this.activeCache.forEach(active => {
                        this.nodes[active].isActive = false;
                        this.updateVnodeState(active);
                        this.activeCache.delete(active);
                    });
                }

                const node = this.nodes[key];
                if (!node) return;

                if (isActive) this.activeCache.add(key);
                else this.activeCache.delete(key);

                node.isActive = isActive;

                this.updateVnodeState(key);
            },
            updateSelected(key, isSelected) {
                if (!this.nodes.hasOwnProperty(key)) return;

                // if(!this.multiple && this.nodes[key].children.length > 0) return;
                const changed = new Map();

                if (!this.multiple) {
                    let _this = this;
                    Object.keys(this.nodes).forEach(function (k) {
                        let objKey = _this.getObjectValueByPath(_this.nodes[k].item, _this.itemKey)
                        _this.nodes[objKey].isSelected = false;
                        _this.updateVnodeState(objKey);
                        _this.selectedCache.delete(objKey);
                        // changed.set(objKey, false);
                    })
                }

                if (this.dataSelectionType !== 'independent') {
                    const descendants = [key, ...this.getDescendants(key)];
                    descendants.forEach(descendant => {
                        this.nodes[descendant].isSelected = isSelected;
                        this.nodes[descendant].isIndeterminate = false;
                        changed.set(descendant, isSelected);
                    });

                    const parents = this.getParents(key);
                    parents.forEach(parent => {
                        this.nodes[parent] = this.calculateState(this.nodes[parent], this.nodes);
                        changed.set(parent, this.nodes[parent].isSelected)
                    })
                } else {
                    this.nodes[key].isSelected = isSelected;
                    this.nodes[key].isIndeterminate = false;
                    changed.set(key, isSelected)
                }

                for (const [key, value] of changed.entries()) {
                    this.updateVnodeState(key);

                    if (this.dataSelectionType === 'leaf' && this.isParent(key) && this.selectionMode !== 'all') continue;

                    value === true ? this.selectedCache.add(key) : this.selectedCache.delete(key)
                }
                this.$emit('selection-change', this.selectedCache, key, isSelected, this.nodes);
            },
            updateOpen(key, isOpen) {
                if (!this.nodes.hasOwnProperty(key)) return;

                const node = this.nodes[key];
                const children = this.getObjectValueByPath(node.item, this.itemChildren);

                if (children && !children.length && node.vnode && !node.vnode.hasLoaded) {
                    node.vnode.checkChildren().then(() => this.updateOpen(key, isOpen));
                } else if (children && children.length) {
                    node.isOpen = isOpen;

                    node.isOpen ? this.openCache.add(key) : this.openCache.delete(key);

                    this.updateVnodeState(key);
                }
            },
            updateVnodeState(key) {
                const node = this.nodes[key];

                if (node && node.vnode) {
                    node.vnode.isSelected = node.isSelected;
                    node.vnode.isIndeterminate = node.isIndeterminate;
                    node.vnode.isActive = node.isActive;
                    node.vnode.isOpen = node.isOpen
                }
            },
            isExcluded(key) {
                return !!this.search && this.excludedItems.has(key)
            },
            getObjectValueByPath(obj, path, fallback = {}) {
                if (obj == null || !path || typeof path !== 'string') return fallback;
                if (obj[path] !== undefined) return obj[path];
                path = path.replace(/\[(\w+)\]/g, '.$1');
                path = path.replace(/^\./, '');
                return this.getNestedValue(obj, path.split('.'), fallback)
            },
            getNestedValue(obj, path, fallback = {}) {
                const last = path.length - 1;
                if (last < 0) return obj === undefined ? fallback : obj;
                for (let i = 0; i < last; i++) {
                    if (obj == null) {
                        return fallback;
                    }
                    obj = obj[path[i]];
                }
                if (obj == null) return fallback;

                return obj[path[last]] === undefined ? fallback : obj[path[last]];
            },
            deepEqual(a, b) {
                if (a === b) return true;

                if (a instanceof Date && b instanceof Date) {
                    // If the values are Date, they were convert to timestamp with getTime and compare it
                    if (a.getTime() !== b.getTime()) return false
                }

                if (a !== Object(a) || b !== Object(b)) {
                    // If the values aren't objects, they were already checked for equality
                    return false
                }

                const props = Object.keys(a);

                if (props.length !== Object.keys(b).length) {
                    // Different number of props, don't bother to check
                    return false
                }

                return props.every(p => this.deepEqual(a[p], b[p]))
            },
            arrayDiff(a, b) {
                const diff = []
                for (let i = 0; i < b.length; i++) {
                    if (a.indexOf(b[i]) < 0) diff.push(b[i])
                }
                return diff
            },
            onAddButtonClick(event) {
                this.$emit('on-add-button-click', event);
            },
            onEditButtonClick(event) {
                this.$emit('on-edit-button-click', event);
            },
            onRemoveButtonClick(event) {
                this.$emit('on-remove-button-click', event);
            },
            log(msg, obj = "") {
                console.log(msg, obj);
            }
        },
    }
</script>

<style scoped lang="scss">

</style>
